import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, UserX, Ban, MessageSquare, ExternalLink } from 'lucide-react'
import {
  useParticipantActionsStore,
  type ParticipantTarget,
  type ParticipantCtx,
} from '../../stores/participantActionsStore'
import { voiceSessionApi, dmApi } from '../../services/api'
import { mediaUrl } from '../../services/config'
import { displayInitial } from '../../utils/displayInitial'
import { BAN_DURATIONS, banParticipant, kickParticipant } from './participantActions'

// Shape of the slice of GET /voice-sessions/user/:id/stats we read here.
// The endpoint returns more (topChannels, dailyStats, recentSessions) — the
// drawer only needs the headline counters + position. See UserPublicProfilePage.
interface UserStats {
  user?: { username?: string; avatarUrl?: string | null; position?: string | null; status?: string | null }
  totalSessions?: number
  totalDuration?: number
}

// Right-edge slide-over shown over an active call. For authed users it loads
// voice-session stats; for guests/PSTN it shows just the identity. Host sees
// inline Kick + Ban; everyone with a userId target gets Message + a link to
// the full profile page (which carries the moderation context via route state).
export default function ParticipantProfileDrawer() {
  const navigate = useNavigate()
  const drawer = useParticipantActionsStore((s) => s.drawer)
  const close = useParticipantActionsStore((s) => s.closeDrawer)
  const [data, setData] = useState<UserStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [banOpen, setBanOpen] = useState(false)

  useEffect(() => {
    if (!drawer?.target.userId) {
      setData(null)
      return
    }
    setLoading(true)
    setBanOpen(false)
    voiceSessionApi
      .userStats(drawer.target.userId)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [drawer?.target.userId])

  // Close on Escape, matching the context menu's behaviour.
  useEffect(() => {
    if (!drawer) return
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', onEsc)
    return () => document.removeEventListener('keydown', onEsc)
  }, [drawer, close])

  if (!drawer) return null
  const { target, ctx } = drawer
  const avatarUrl = target.avatarUrl ?? data?.user?.avatarUrl
  const fmt = (s: number) => {
    const h = Math.floor(s / 3600)
    const m = Math.floor((s % 3600) / 60)
    return h > 0 ? `${h}h ${m}m` : `${m}m`
  }

  const startDM = async () => {
    if (!target.userId) return
    try {
      const r = await dmApi.create([target.userId])
      navigate(`/channels/dm-${r.data.id}`)
    } catch (e) {
      console.error('[ParticipantProfileDrawer] DM failed', e)
    }
    close()
  }

  const kick = async () => {
    try {
      await kickParticipant(ctx, target)
    } catch (e) {
      console.error('[ParticipantProfileDrawer] kick failed', e)
    }
    close()
  }

  const openFullProfile = () => {
    if (!target.userId) return
    navigate(`/users/${target.userId}`, {
      state: {
        canManage: ctx.isHost,
        channelId: ctx.channelId,
        serverId: ctx.serverId,
        voiceChannels: ctx.voiceChannels,
      },
    })
    close()
  }

  return (
    <div className="fixed inset-0 z-[150]" onClick={close}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="absolute top-0 right-0 h-full w-[400px] max-w-[92vw] bg-card border-l border-border shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-14 px-5 flex items-center justify-between border-b border-border">
          <span className="text-sm font-semibold text-foreground">Profile</span>
          <button
            onClick={close}
            aria-label="Close profile"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-5">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold overflow-hidden">
              {avatarUrl ? (
                <img src={mediaUrl(avatarUrl)} alt="" className="w-full h-full object-cover" />
              ) : (
                displayInitial(target.displayName)
              )}
            </div>
            <div className="min-w-0">
              <div className="text-base font-bold text-foreground truncate">{target.displayName}</div>
              {data?.user?.position && (
                <div className="text-xs text-muted-foreground truncate">{data.user.position}</div>
              )}
              {target.subjectType !== 'USER' && (
                <div className="text-[11px] text-muted-foreground uppercase">{target.subjectType}</div>
              )}
            </div>
          </div>

          {target.subjectType === 'USER' && (
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-muted/50 p-3">
                <div className="text-lg font-semibold text-foreground tabular-nums">
                  {loading ? '…' : data?.totalSessions ?? 0}
                </div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Sessions</div>
              </div>
              <div className="rounded-xl bg-muted/50 p-3">
                <div className="text-lg font-semibold text-foreground tabular-nums">
                  {loading ? '…' : fmt(data?.totalDuration ?? 0)}
                </div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Total time</div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {target.subjectType === 'USER' && target.userId && (
              <button
                onClick={startDM}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition text-sm font-medium"
              >
                <MessageSquare size={15} /> Message
              </button>
            )}
            {ctx.isHost && (
              <>
                <button
                  onClick={kick}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-destructive/10 text-destructive hover:bg-destructive/20 transition text-sm font-medium"
                >
                  <UserX size={15} /> Kick
                </button>
                <button
                  onClick={() => setBanOpen((v) => !v)}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-destructive/10 text-destructive hover:bg-destructive/20 transition text-sm font-medium"
                >
                  <Ban size={15} /> Ban…
                </button>
                {banOpen && <BanInline target={target} ctx={ctx} onDone={close} />}
              </>
            )}
            {target.subjectType === 'USER' && target.userId && (
              <button
                onClick={openFullProfile}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-muted text-foreground hover:bg-muted/70 transition text-sm font-medium"
              >
                <ExternalLink size={15} /> Open full profile
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// Inline ban controls (reason + duration grid) revealed under the Ban button.
function BanInline({
  target,
  ctx,
  onDone,
}: {
  target: ParticipantTarget
  ctx: ParticipantCtx
  onDone: () => void
}) {
  const [reason, setReason] = useState('')
  const ban = async (minutes: number | null) => {
    try {
      await banParticipant(ctx, target, minutes, reason)
    } catch (e) {
      console.error('[ParticipantProfileDrawer] ban failed', e)
    }
    onDone()
  }
  return (
    <div className="rounded-lg bg-muted/40 p-2 space-y-2">
      <input
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Reason (optional)"
        className="w-full px-2 py-1 text-xs rounded-md bg-muted text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-primary"
      />
      <div className="grid grid-cols-6 gap-1">
        {BAN_DURATIONS.map((d) => (
          <button
            key={d.short}
            onClick={() => ban(d.minutes)}
            className="px-1 py-1 text-[11px] rounded bg-muted text-foreground hover:bg-destructive hover:text-destructive-foreground transition"
          >
            {d.short}
          </button>
        ))}
      </div>
    </div>
  )
}
